import { useState, useEffect } from 'react';
import Chatbot from './Chatbot';
import { isIosApp, isWebView } from '../../Utilities';

// ── Inject SCSS-like styles ──────────────────────────────────────────────────
const styles = `
  /* ---- CSS custom properties ---- */
  :root {
   
    --cb-surface:       #0f172a;
    --cb-panel-bg:      #ffffff;
    --cb-panel-radius:  20px;
    --cb-btn-size:      64px;
    --cb-shadow-rest:   0 8px 32px rgba(79,70,229,.45);
    --cb-shadow-hover:  0 12px 48px rgba(99,102,241,.65);
    --cb-panel-shadow:  0 24px 64px rgba(15,23,42,.22);
    --cb-transition:    .35s cubic-bezier(.34,1.56,.64,1);
  }

  /* ---- Floating button wrapper ---- */
 .ChatbotButton__wrapper {
  position: fixed;
  bottom: ${isIosApp ? '120px' : '80px'};
  right: 32px;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;
  pointer-events: none;   /* ← wrapper never blocks scroll */
}

/* Re-enable pointer events only on the FAB and open panel */
.ChatbotButton__fab,
.ChatbotButton__panel--open {
  pointer-events: all;
}
  
  /* ---- Panel container ---- */
.ChatbotButton__panel {
  background: var(--cb-panel-bg);
  border-radius: var(--cb-panel-radius);
  box-shadow: var(--cb-panel-shadow);
  width: 380px;
  min-height: 520px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transform-origin: bottom right;
  transition: opacity var(--cb-transition), transform var(--cb-transition), min-height var(--cb-transition);
  opacity: 0;
  transform: scale(.88) translateY(12px);
  pointer-events: none;
  min-height: 0;        /* ← collapse height when closed */
  height: 0;            /* ← remove from flow entirely */
}

.ChatbotButton__panel.ChatbotButton__panel--open {
  opacity: 1;
  transform: scale(1) translateY(0);
  pointer-events: all;
  min-height: 520px;    /* ← restore when open */
  height: auto;
}

  /* ---- Panel header ---- */
  .ChatbotButton__panel-header {
    background: linear-gradient(135deg, var(--material-dark) 0%, var(--material-light) 100%);
    padding: 18px 20px;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }

  .ChatbotButton__panel-avatar {
    width: 36px;
    height: 36px;
    background: rgba(255,255,255,.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .ChatbotButton__panel-title {
    flex: 1;
    color: #fff;
    font-family: 'Segoe UI', system-ui, sans-serif;
  }

  .ChatbotButton__panel-title h6 {
    margin: 0;
    font-size: .95rem;
    font-weight: 700;
    letter-spacing: .01em;
  }

  .ChatbotButton__panel-title span {
    font-size: .75rem;
    opacity: .85;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .ChatbotButton__status-dot {
    width: 7px;
    height: 7px;
    background: #4ade80;
    border-radius: 50%;
    display: inline-block;
    animation: ChatbotButton__pulse-dot 2s ease-in-out infinite;
  }

  .ChatbotButton__close-btn {
    background: rgba(255,255,255,.18);
    border: none;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    color: #fff;
    font-size: 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background .2s ease;
    flex-shrink: 0;
  }

  .ChatbotButton__close-btn:hover {
    background: rgba(255,255,255,.32);
  }

  /* ---- Panel body ---- */
  .ChatbotButton__panel-body {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  /* ---- FAB button ---- */
  .ChatbotButton__fab {
    width: var(--cb-btn-size);
    height: var(--cb-btn-size);
    border-radius: 50%;
    border: none;
    cursor: pointer;
    background: linear-gradient(135deg, var(--material-light) 0%, var(--material-dark) 100%);
    box-shadow: var(--cb-shadow-rest);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    transition: box-shadow var(--cb-transition), transform var(--cb-transition);
    outline: none;
    flex-shrink: 0;
  }

  .ChatbotButton__fab:hover {
    box-shadow: var(--cb-shadow-hover);
    transform: scale(1.08) translateY(-3px);
  }

  .ChatbotButton__fab:active {
    transform: scale(.96);
  }

  /* Ripple ring */
  .ChatbotButton__fab::before {
    content: '';
    position: absolute;
    inset: -6px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--lighter-blue), var(--lightest-blue));
    opacity: 0;
    z-index: -1;
    animation: ChatbotButton__ring-idle 3s ease-in-out infinite;
  }

  .ChatbotButton__fab-icon {
    transition: transform var(--cb-transition), opacity .25s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ChatbotButton__fab-icon--hidden {
    opacity: 0;
    transform: rotate(90deg) scale(.6);
    position: absolute;
  }

  .ChatbotButton__fab-icon--visible {
    opacity: 1;
    transform: rotate(0deg) scale(1);
  }

  /* ---- Tooltip ---- */
  .ChatbotButton__tooltip {
    position: absolute;
    right: calc(100% + 14px);
    top: 50%;
    transform: translateY(-50%);
    background: var(--cb-surface);
    color: #e2e8f0;
    font-family: 'Segoe UI', system-ui, sans-serif;
    font-size: .78rem;
    font-weight: 500;
    padding: 6px 12px;
    border-radius: 8px;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    transition: opacity .2s ease, transform .2s ease;
    transform: translateY(-50%) translateX(6px);
  }

  .ChatbotButton__tooltip::after {
    content: '';
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 5px solid transparent;
    border-left-color: var(--cb-surface);
  }

  .ChatbotButton__fab:hover .ChatbotButton__tooltip {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
  }

  /* ---- Badge (notification dot) ---- */
  .ChatbotButton__badge {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 14px;
    height: 14px;
    background: #f43f5e;
    border: 2.5px solid #fff;
    border-radius: 50%;
    animation: ChatbotButton__badge-pop .4s var(--cb-transition) both;
  }

  /* ---- Keyframes ---- */
  @keyframes ChatbotButton__ring-idle {
    0%, 100% { opacity: 0; transform: scale(1); }
    50%       { opacity: .35; transform: scale(1.15); }
  }

  @keyframes ChatbotButton__pulse-dot {
    0%, 100% { box-shadow: 0 0 0 0 rgba(74,222,128,.5); }
    50%       { box-shadow: 0 0 0 5px rgba(74,222,128,0); }
  }

  @keyframes ChatbotButton__badge-pop {
    from { transform: scale(0); }
    to   { transform: scale(1); }
  }

  /* ---- Responsive ---- */
  @media (max-width: 480px) {
    .ChatbotButton__panel {
      width: calc(100vw - 24px);
      min-height: 460px;
      right: 12px;
    }
   
  }
`;

// ── SVG Icons ────────────────────────────────────────────────────────────────
const RobotIcon = () => (
  <img
    src='https://d1l33ng5cy7vc.cloudfront.net/Production/2050/Utilities/chatbot.png'
    className='w-100'
  />
);

const CloseIcon = () => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M1 1L13 13M13 1L1 13' stroke='white' strokeWidth='2' strokeLinecap='round' />
  </svg>
);

export default function ChatbotButton({ showBadge = true, defaultOpen = false, history }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [badgeVisible, setBadgeVisible] = useState(showBadge);

  // Remove badge when panel is first opened
  useEffect(() => {
    if (isOpen) setBadgeVisible(false);
  }, [isOpen]);

  return (
    <>
      {/* Inject component styles */}
      <style>{styles}</style>

      <div className={'ChatbotButton__wrapper'}>
        {/* ── Slide-up panel ── */}
        <div
          className={`ChatbotButton__panel shadow-lg${
            isOpen ? ' ChatbotButton__panel--open' : ''
          }`}
          role='dialog'
          aria-label='AI Assistant'
          aria-hidden={!isOpen}
        >
          {isOpen && <Chatbot isWrapper={window.screen.width >= 768} history={history} />}
        </div>

        {/* ── Floating Action Button ── */}
        <button
          className='ChatbotButton__fab'
          onClick={() => {
            if (window.screen.width < 768) {
              history.push('/chatbot');
            } else {
              setIsOpen((prev) => !prev);
            }
          }}
          aria-label={isOpen ? 'Close AI assistant' : 'Open AI assistant'}
          aria-expanded={isOpen}
        >
          {/* Idle robot icon */}
          <span
            className={`ChatbotButton__fab-icon ${
              isOpen ? 'ChatbotButton__fab-icon--hidden' : 'ChatbotButton__fab-icon--visible'
            }`}
            aria-hidden='true'
          >
            <RobotIcon />
          </span>

          {/* Close icon (shown when panel is open) */}
          <span
            className={`ChatbotButton__fab-icon ${
              isOpen ? 'ChatbotButton__fab-icon--visible' : 'ChatbotButton__fab-icon--hidden'
            }`}
            aria-hidden='true'
          >
            <CloseIcon />
          </span>

          {/* Notification badge */}
          {badgeVisible && !isOpen && (
            <span className='ChatbotButton__badge' aria-label='New message' />
          )}

          {/* Tooltip */}
          {!isOpen && (
            <span className='ChatbotButton__tooltip' role='tooltip'>
              Chat with AI Assistant ✨
            </span>
          )}
        </button>
      </div>
    </>
  );
}
